/**
 * Snackbar Component - Constants
 * ================================
 * Tipos y mapas de estilo del snackbar (Figma: node 1627:2382). A diferencia
 * de Alert, la estructura de Figma es fija (título opcional + texto + una
 * sola acción de texto) y no requiere composición libre — por eso es un leaf
 * con bindings (`title`/`text`/`actionLabel`), no un compound component.
 *
 * Nota de fidelidad: Figma reporta el título con el mismo peso "Regular"
 * (400) que el texto, pero la captura visual del frame lo muestra en negrita
 * — se prioriza la captura y se usa `font-semibold` para el título.
 */

// ============================================================================
// TYPES
// ============================================================================

export type SnackbarMode = 'dark' | 'light';

/**
 * Posicion en la ventana. Es OPCIONAL: sin `position` el snackbar no se
 * posiciona solo y fluye en el layout del consumidor, como hasta v1.2.0.
 *
 * Se implementa por SELECTOR DE ATRIBUTO en CSS (`ec-snackbar[position]`, ver
 * styles/main.css y styles/cdn.css) y NO por clases aplicadas desde el
 * controller. El motivo es concreto: aplicarlas requeriria $onInit, y sobre el
 * Angular 1.5.5 de la app host los defaults de $onInit no quedan aplicados, asi
 * que la posicion no funcionaria justo para el consumidor que la necesita. El
 * CSS lee el atributo del DOM y es inmune a eso.
 *
 * La distancia al borde sale de tokens, porque depende del host (easycancha lo
 * apoya sobre una navbar inferior de 50px):
 *   --ec-snackbar-offset  borde superior/inferior  (default 1rem)
 *   --ec-snackbar-inset   borde lateral            (default 1rem)
 *   --ec-snackbar-z       z-index                  (default 1060)
 */
export type SnackbarPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface SnackbarModeStyles {
  container: string;
  title: string;
  text: string;
  action: string;
}

export interface SnackbarDefaultValues {
  mode: SnackbarMode;
  actionLabel: string;
}

// ============================================================================
// MODE (Figma "mode": dark | light)
// ============================================================================

export const MODE_CLASSES: Record<SnackbarMode, SnackbarModeStyles> = {
  dark: {
    container: 'ec-bg-color-surface-inverse',
    title: 'ec-text-white',
    // #B3B3B3 (Figma gray/200) coincide exactamente con el token existente
    // color-border (mismo hex, pensado para bordes) — se reutiliza acá como
    // color de texto muted en vez de sumar un token duplicado.
    text: 'ec-text-color-border',
    action: 'ec-text-color-primary-light'
  },
  light: {
    container: 'ec-bg-color-surface ec-border ec-border-color-border',
    title: 'ec-text-color-text',
    // #666666 (Figma gray/500) coincide exactamente con el token de paleta gray.
    text: 'ec-text-gray',
    action: 'ec-text-color-primary'
  }
};

// ============================================================================
// BASE
// ============================================================================

export const BASE_CLASSES = {
  container: 'ec-flex ec-items-center ec-gap-3 ec-w-full ec-max-w-[358px] ec-p-4 ec-rounded-2xl ec-font-main',
  content: 'ec-flex ec-flex-col ec-flex-1 ec-min-w-0 ec-gap-1',
  title: 'ec-text-base ec-font-semibold ec-tracking-[-0.48px] ec-truncate ec-w-full',
  text: 'ec-text-base ec-font-normal ec-tracking-[-0.48px] ec-line-clamp-3 ec-w-full',
  action:
    'ec-inline-flex ec-items-center ec-justify-center ec-shrink-0 ec-rounded-lg ec-min-h-[40px] ec-pl-3 ec-py-2 ' +
    'ec-text-sm ec-font-semibold ec-tracking-[-0.42px] ec-bg-transparent ec-border-0 ec-cursor-pointer ' +
    'hover:ec-opacity-80 focus-visible:ec-outline-none focus-visible:ec-ring-1 focus-visible:ec-ring-current'
};

// ============================================================================
// DEFAULTS
// ============================================================================

export const DEFAULTS: SnackbarDefaultValues = {
  mode: 'dark',
  actionLabel: 'Acción'
};
